<script setup>
import { HrBtn } from '../primitives';

/**
 * Primary page-level call-to-action — the highest-emphasis action in a toolbar or page header.
 *
 * @status recommended
 * @group actions
 * @useWhen Triggering the primary action of a page (e.g. Create, Save, Submit)
 * @avoidWhen The action is secondary — use HrPageActionSecondaryBtn
 * @related HrPageActionSecondaryBtn
 * @a11y Native button; activates on Enter and Space.
 */
defineOptions({ name: 'HrPageActionBtn' });

const emit = defineEmits({
  /** Fired when the user activates the button. */
  click: null,
});

defineProps({
  /**
   * Button text label. When null the default slot provides the content.
   * @replacesSlot default
   */
  label: {
    type: String,
    default: null,
  },
  /** Icon displayed to the left of the label. */
  icon: {
    type: String,
    default: null,
  },
  /** Shows a loading spinner in place of the label. */
  loading: {
    type: Boolean,
    default: false,
  },
  /** Prevents interaction and applies a dimmed appearance. */
  disable: {
    type: Boolean,
    default: false,
  },
  /**
   * Dimmed and inert like `disable`, but still hoverable and focusable, so a tooltip in the slot
   * can say why the action is not available.
   */
  unavailable: {
    type: Boolean,
    default: false,
  },
  /** Renders a pill-shaped skeleton placeholder instead of the button. */
  pending: {
    type: Boolean,
    default: false,
  },
  /** Vue Router route location. Renders the button as a router-link. */
  to: {
    type: [String, Object],
    default: null,
  },
  /** Replace the current history entry instead of pushing a new one. */
  replace: {
    type: Boolean,
    default: false,
  },
  /** Native anchor href. Use for external links; prefer `to` for in-app navigation. */
  href: {
    type: String,
    default: null,
  },
  /** Native anchor target (e.g. `_blank`). Only meaningful when `href` is set. */
  target: {
    type: String,
    default: null,
  },
  /**
   * Native button type.
   * @values button, submit, reset
   */
  type: {
    type: String,
    default: 'button',
  },
});

// @todo Remove legacy-props when Legacy theme is removed
const legacyProps = { color: 'primary' };
</script>
<template>
  <hr-btn
    variant="filled"
    :legacy-props="legacyProps"
    v-bind="$props"
    @click="emit('click', $event)"
  >
    <!-- @slot Button label / content. -->
    <slot />
  </hr-btn>
</template>
